import { ProductSettings, ProductVariantInput } from '@shopify-multiseller/graphql/partner/types';
import { calculateLineItemFees } from '@shopify-multiseller/common';
import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { SkeletonPanel, SkeletonText } from '../../../components';
import { Card } from '../../../components/primitives';
import { SectionCard } from '../../../components/SectionCard';
import { formatCents } from '../../../helpers/formatters';
import { useSettings, useShopCurrency } from '../../../hooks';

export interface ProductFeesSectionProps {
  /** Optional: a product mid-edit can momentarily have no variant to price. */
  variant?: ProductVariantInput;
  /**
   * This product's own fee settings, when the server resolved any.
   *
   * The shop can override commission and fees per product, and those overrides
   * are what an order is actually charged — so the card that states the deal
   * has to read them rather than the seller's defaults. Absent on a product
   * being created, which has no id to resolve overrides against yet.
   */
  productSettings?: Pick<ProductSettings, 'productFee' | 'productPercentage' | 'commissionBasis'> | null;
  hideTitle?: boolean;
  hidePayout?: boolean;
  hideTerms?: boolean;
  loading?: boolean;
}

/** One term of the deal: what it is on the left, what it costs on the right. */
const TermRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-semibold text-foreground">{value}</span>
  </div>
);

const ProductFeesSection: React.FC<ProductFeesSectionProps> = ({ variant, productSettings, loading = false, hidePayout = false, hideTerms = false , hideTitle= false}) => {

  // Hooks must run before any early return so hook order stays stable
  // across renders (React rules of hooks).
  const settings = useSettings();
  const currencyCode = useShopCurrency();

  if (loading) return <ProductFeesSectionLoader />;

  // Product scope wins where it exists, seller scope otherwise — the same
  // order the server resolves them in. Read from the seller alone, this card
  // quoted a rate the order would not charge.
  const productFeeSetting = productSettings?.productFee ?? settings.productFee;
  const productPercentageSetting = productSettings?.productPercentage ?? settings.productPercentage;
  const returnsSetting = settings.returns;

  if (!productFeeSetting && !productPercentageSetting && !returnsSetting) {
    return null;
  }

  // Integer cents, like every other money figure on this card (MPPRO-45).
  const price: number = variant?.price || 0;

  // S10: when the merchant charges commission on PROFIT, this estimate can't be computed
  // here — the commission depends on the product's cost of goods, which lives in Shopify
  // and isn't part of the seller's product form. Showing the net-sales figure anyway would
  // state a number the seller will not actually receive, so explain the basis instead.
  const isProfitBasis =
    (productSettings?.commissionBasis ?? settings.commissionBasis)?.basis === 'PROFIT';

  // MPPRO-79: the same function the order-creation webhook charges with, rather than the
  // arithmetic re-typed here. A "You receive" figure that disagrees with what the seller
  // is actually billed is the bug this card exists to avoid. Quantity is 1 because this
  // is a per-unit quote — see the caption below.
  //
  // MPPRO-45 finished what MPPRO-131 started here: `price` and `productFee`
  // are both integer cents now, which is the unit `calculateLineItemFees`
  // documents for both of its money arguments, so neither is converted on the
  // way in and the figure below renders with `formatCents`. The conversion
  // that used to bring the fee down to dollars is gone — it was there only
  // because `variant.price` was dollars, and keeping it would now subtract
  // dollars from cents, the same mixed-unit estimate in the other direction.
  const { totalFees } = calculateLineItemFees({
    unitPrice: price,
    quantity: 1,
    productPercentage: productPercentageSetting?.productPercentage || 0,
    productFee: productFeeSetting?.productFee || 0,
  });
  const payoutAmount = price - totalFees;

  const isNilPayout = payoutAmount <= 0;
  const hasCharges = !!productFeeSetting?.productFee || !!productPercentageSetting?.productPercentage;

  const terms = hasCharges ? (
    // The two charges as a pair of rows rather than a bordered list of
    // sentences: the seller is comparing numbers, so the numbers line up.
    <div>
      <TermRow
        label="Commission"
        value={
          productPercentageSetting?.productPercentage
            ? `${productPercentageSetting.productPercentage}% of product total`
            : 'None'
        }
      />
      <TermRow
        label="Product fee"
        value={
          productFeeSetting?.productFee
            ? formatCents({ amount: productFeeSetting.productFee, currencyCode })
            : 'None'
        }
      />
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
      <span className="text-sm text-foreground">No fees or commission</span>
    </div>
  );

  // Which variation this figure is for. A product with variations has one
  // price per variation, so an unqualified "You receive $80.10" beside a
  // catalogue priced 89–109 is a number the seller cannot place.
  const variationName = (variant?.variantOptions ?? [])
    .map((option) => option.value)
    .filter(Boolean)
    .join(' / ');

  const payout = (
    <div className="rounded-md bg-muted/50 p-3.5">
      <p className="text-sm text-muted-foreground">
        {variationName ? `You receive (${variationName})` : 'You receive'}
      </p>
      {isProfitBasis ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Commission of {productPercentageSetting?.productPercentage || 0}% is charged on profit
          (sale price minus cost of goods) rather than on the sale price, so the exact amount
          depends on this product&apos;s cost. You&apos;ll see the full breakdown on each order.
        </p>
      ) : (
        <>
          {/* The symbol, like every other amount in the app — this was the one
              place printing "USD 19.99" instead of "$19.99". */}
          <p className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">
            {formatCents({ amount: isNilPayout ? 0 : payoutAmount, currencyCode })}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {variationName
              ? 'Per unit, after commission. Varies by variation price.'
              : `Per unit, after commission. Amounts are in ${currencyCode}.`}
          </p>
        </>
      )}
    </div>
  );

  // Inside the variant editor the block is a plain panel — it already sits
  // under that dialog's own heading, so a second title would repeat it.
  if (hideTitle) {
    return (
      <Card className="space-y-4 overflow-hidden p-5">
        {!hideTerms && terms}
        {!hidePayout && payout}
      </Card>
    );
  }

  return (
    <SectionCard title="Fees & commission" testId="product-fees-section">
      <div className="space-y-4">
        {!hideTerms && terms}
        {!hidePayout && payout}
      </div>
    </SectionCard>
  );
};

const ProductFeesSectionLoader: React.FC = () => {
  return (
    <SkeletonPanel title={true}>
      <SkeletonPanel.Section>
        <div className="grid grid-cols-4 gap-6">
          <div className='col-span-4 sm:col-span-2'>
            <SkeletonText lines={1} width={100} />
          </div>
        </div>
      </SkeletonPanel.Section>
    </SkeletonPanel>
  );
};

export default ProductFeesSection;
